import { openDB, type IDBPDatabase } from 'idb';
import type { InMemoryCache, NormalizedCacheObject } from '@apollo/client';

const DB_NAME = 'scio3';
const STORE_NAME = 'cache';
const CACHE_KEY = 'apollo-cache';
const VERSION_KEY = 'cache-version';

/**
 * Bump this when GraphQL schema changes in a way that makes cached data
 * incompatible (e.g. renamed fields, changed __typename). Forces a full
 * cache clear on next app load.
 */
const CACHE_VERSION = 3;

let db: IDBPDatabase | null = null;

async function getDb(): Promise<IDBPDatabase> {
  if (!db) {
    db = await openDB(DB_NAME, 1, {
      upgrade(database) {
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME);
        }
      },
    });
  }
  return db;
}

/**
 * Restore Apollo cache from IndexedDB.
 * Call before creating ApolloClient so the cache is pre-hydrated.
 * Returns true if cache was restored, false if empty/cleared.
 *
 * Cross-user safety: AuthProvider compares `scio-cache-owner` (localStorage)
 * with the current Firebase user. On mismatch, it calls clearStore() +
 * clearPersistedCache() before any user-specific data renders.
 */
export async function restoreCache(cache: InMemoryCache): Promise<boolean> {
  try {
    const store = await getDb();
    const storedVersion = await store.get(STORE_NAME, VERSION_KEY);
    if (storedVersion !== CACHE_VERSION) {
      await store.clear(STORE_NAME);
      await store.put(STORE_NAME, CACHE_VERSION, VERSION_KEY);
      return false;
    }
    const data = (await store.get(STORE_NAME, CACHE_KEY)) as NormalizedCacheObject | undefined;
    if (data) {
      cache.restore(data);
      return true;
    }
    return false;
  } catch {
    // IndexedDB unavailable (private browsing, quota) — continue without cache
    return false;
  }
}

/**
 * Persist current Apollo cache to IndexedDB.
 * Called on a debounced timer and when the tab becomes hidden.
 */
export async function persistCache(cache: InMemoryCache): Promise<void> {
  try {
    const store = await getDb();
    const data = cache.extract();
    await store.put(STORE_NAME, data, CACHE_KEY);
    await store.put(STORE_NAME, CACHE_VERSION, VERSION_KEY);
  } catch {
    // Silently fail — persistence is best-effort
  }
}

/**
 * Clear all persisted cache data. Call on logout and team switch.
 */
export async function clearPersistedCache(): Promise<void> {
  try {
    const store = await getDb();
    await store.clear(STORE_NAME);
  } catch {
    // Silently fail
  }
}
